import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, delay, exhaustMap, map, mergeMap } from 'rxjs/operators';
import { ConnectionAdapter } from '../../adapters/connection.adapter';
import {
  deleteConnection,
  deleteConnectionFailure,
  deleteConnectionSuccess,
  loadConnection,
  loadConnectionFailure,
  loadConnectionJobs,
  loadConnectionJobsFailure,
  loadConnectionJobsSuccess,
  loadConnectionSuccess,
  syncConnection,
  syncConnectionFailure,
  syncConnectionSuccess,
  updateConnection,
  updateConnectionFailure,
  updateConnectionSuccess,
} from './connection.actions';

@Injectable()
export class ConnectionEffects {
  private readonly actions$ = inject(Actions);
  private readonly connectionAdapter = inject(ConnectionAdapter);

  loadConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadConnection),
      exhaustMap(() =>
        this.connectionAdapter.getConnection().pipe(
          map((connection) => loadConnectionSuccess({ connection })),
          catchError((error) => {
            return of(loadConnectionFailure({ error }));
          })
        )
      )
    )
  );

  loadConnectionJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadConnectionJobs),
      exhaustMap(() =>
        this.connectionAdapter.getConnectionJobs().pipe(
          map((response) => loadConnectionJobsSuccess({ jobs: response.jobs })),
          catchError((error) => {
            return of(loadConnectionJobsFailure({ error }));
          })
        )
      )
    )
  );

  updateConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateConnection),
      mergeMap((props) =>
        this.connectionAdapter.editConnection(props.connection).pipe(
          map(() => updateConnectionSuccess({ connection: props.connection })),
          catchError((error) => {
            return of(updateConnectionFailure({ error }));
          })
        )
      )
    )
  );

  deleteConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteConnection),
      mergeMap(() =>
        this.connectionAdapter.deleteConnection().pipe(
          map(() => deleteConnectionSuccess()),
          catchError((error) => {
            return of(deleteConnectionFailure({ error }));
          })
        )
      )
    )
  );

  syncConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(syncConnection),
      mergeMap(() =>
        this.connectionAdapter.syncConnection().pipe(
          delay(5000),
          map(() => syncConnectionSuccess()),
          catchError((error) => {
            return of(syncConnectionFailure({ error }));
          })
        )
      )
    )
  );
}
